import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from "@chakra-ui/react";
import { Editor } from "@tiptap/react";
import useToast from "../../../../utilities/useToast";

interface RichTextLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  editor: Editor | null;
}

export const RichTextLinkModal: React.FC<RichTextLinkModalProps> = ({
  isOpen,
  onClose,
  editor,
}) => {
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [errors, setErrors] = useState({ url: false, text: false });
  const { displayToast } = useToast();

  useEffect(() => {
    if (!isOpen || !editor) return;

    const { selection } = editor.state;
    const selectedText = editor.state.doc.textBetween(
      selection.from,
      selection.to,
    );
    const linkAttributes = editor.getAttributes("link");
    const hasLink = editor.isActive("link");

    if (hasLink && linkAttributes.href) {
      setIsEdit(true);
      setUrl(linkAttributes.href);
      setText(selectedText || "");
    } else {
      setIsEdit(false);
      setUrl("");
      setText(selectedText || "");
    }

    setErrors({ url: false, text: false });
  }, [isOpen, editor]);

  const isValidUrl = (urlString: string): boolean => {
    const trimmed = urlString.trim();
    if (!trimmed) return false;
    if (
      trimmed.startsWith("/") ||
      trimmed.startsWith("#") ||
      trimmed.startsWith("mailto:") ||
      trimmed.startsWith("tel:")
    )
      return true;

    try {
      const processedUrl =
        trimmed.startsWith("http://") || trimmed.startsWith("https://")
          ? trimmed
          : `https://${trimmed}`;

      const urlObject = new URL(processedUrl);

      if (urlObject.hostname.length === 0) return false;

      const hostname = urlObject.hostname.toLowerCase();
      const domainPattern =
        /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,}$/;

      return domainPattern.test(hostname);
    } catch {
      return false;
    }
  };

  const processUrl = (urlString: string): string => {
    const trimmed = urlString.trim();
    return /^(https?:\/\/|mailto:|tel:|\/|#)/.test(trimmed)
      ? trimmed
      : `https://${trimmed}`;
  };

  const handleClose = () => {
    setUrl("");
    setText("");
    setIsEdit(false);
    setErrors({ url: false, text: false });
    onClose();
  };

  const handleSave = () => {
    setErrors({ url: false, text: false });

    if (!editor) return;

    const urlError = !url.trim();
    const textError = !text.trim();
    const urlValidError = !!url.trim() && !isValidUrl(url);

    if (urlError || textError || urlValidError) {
      setErrors({
        url: urlError || urlValidError,
        text: textError,
      });

      const getErrorType = () => {
        if (urlError && textError) return "both";
        if (urlError) return "url";
        if (textError) return "text";
        if (urlValidError) return "invalid";
        return "none";
      };

      switch (getErrorType()) {
        case "both":
          displayToast({
            title: "Missing Information",
            description: "Please enter both URL and link text",
            status: "error",
          });
          break;

        case "url":
          displayToast({
            title: "Missing URL",
            description: "Please enter a URL",
            status: "error",
          });
          break;

        case "text":
          displayToast({
            title: "Missing Link Text",
            description: "Please enter link text",
            status: "error",
          });
          break;

        case "invalid":
          displayToast({
            title: "Invalid URL",
            description: "Please enter a valid URL",
            status: "error",
          });
          break;

        default:
          displayToast({
            title: "Error",
            description: "An unexpected error occurred",
            status: "error",
          });
          break;
      }
      return;
    }

    const finalUrl = processUrl(url);
    const finalText = text.trim();

    try {
      if (isEdit) {
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .updateAttributes("link", { href: finalUrl })
          .insertContent(finalText)
          .run();
      } else {
        const { from, to } = editor.state.selection;

        if (from !== to) {
          editor.chain().focus().setLink({ href: finalUrl }).run();
        } else {
          editor
            .chain()
            .focus()
            .insertContent(`<a href="${finalUrl}">${finalText}</a>`)
            .run();
        }
      }

      displayToast({
        title: `Link ${isEdit ? "updated" : "added"}`,
        status: "success",
      });

      handleClose();
    } catch (error) {
      displayToast({
        title: "Error",
        description: "Failed to save link",
        status: "error",
      });
    }
  };

  const handleRemove = () => {
    if (!editor) return;

    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    displayToast({
      title: "Link removed",
      status: "success",
    });
    handleClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
    if (errors.url) {
      setErrors((prev) => ({ ...prev, url: false }));
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
    if (errors.text) {
      setErrors((prev) => ({ ...prev, text: false }));
    }
  };

  const getUrlErrorMessage = () => {
    if (!url.trim()) return "URL is required";
    if (!isValidUrl(url)) return "Please enter a valid URL";
    return "";
  };

  const getTextErrorMessage = () => {
    if (!text.trim()) return "Link text is required";
    return "";
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <chakra-scope>
          <ModalHeader>{isEdit ? "Edit Link" : "Add Link"}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={errors.url}>
                <FormLabel fontSize="sm" fontWeight="medium">
                  URL
                </FormLabel>
                <Input
                  value={url}
                  onChange={handleUrlChange}
                  placeholder="https://example.com"
                  onKeyDown={handleKeyDown}
                  autoFocus
                />
                <FormErrorMessage>{getUrlErrorMessage()}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.text}>
                <FormLabel fontSize="sm" fontWeight="medium">
                  Link Text
                </FormLabel>
                <Input
                  value={text}
                  onChange={handleTextChange}
                  placeholder="Enter link text"
                  onKeyDown={handleKeyDown}
                />
                <FormErrorMessage>{getTextErrorMessage()}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3} w="full" justify="space-between">
              {isEdit && (
                <Button
                  variant="ghost"
                  colorScheme="red"
                  onClick={handleRemove}>
                  Remove
                </Button>
              )}
              <HStack spacing={3} ml="auto">
                <Button variant="ghost" onClick={handleClose}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  {isEdit ? "Update" : "Add"}
                </Button>
              </HStack>
            </HStack>
          </ModalFooter>
        </chakra-scope>
      </ModalContent>
    </Modal>
  );
};

export default RichTextLinkModal;
